import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import { Box, Card, CardActions, Collapse, IconButton, Stack, Typography } from "@mui/material";

import { useCustomQuestionFieldDelete } from "../../../data/CustomWorkflowQuestion/useCustomQuestionFieldDelete";
import { useCustomQuestionFieldUpdate } from "../../../data/CustomWorkflowQuestion/useCustomQuestionFieldUpdate";
import { UTP2CustomQuestionTypes } from "../../../shared/enums/UTP2";
import DeleteConfirmationButton from "../../../shared/FormLayout/Buttons/DeleteConfirmationButton";
import { EditButton } from "../../../shared/FormLayout/IconButtons/EditButton";
import {
  convertReusableQuestionToWorkflowQuestionField,
  convertWorkflowQuestionFieldToReusableQuestion,
} from "../../../shared/helpers/CustomQuestionFieldUtils";
import { ReusableCustomQuestionField } from "../../../shared/interfaces/ReusableCustomQuestionField";
import { CustomQuestionField } from "../../../shared/interfaces/Workflow";
import { CustomQuestionBuilderDialog } from "../../WorkflowDetail/CustomQuestionBuilder/CustomQuestionBuilderDialog";

interface Props {
  customerId: number;
  customQuestionField: ReusableCustomQuestionField;
  displayMode?: "view" | "edit" | "select";
  canEditWorkflow?: boolean;
  onSuccess?: (successMessage: string) => void;
  onError?: (errorMessage: string) => void;
  isSelected?: boolean;
  onSelectionChange?: (selectedQuestion: ReusableCustomQuestionField) => void;
}

export function ReusableCustomQuestionCard(props: Props) {
  const { t } = useTranslation();

  const { customerId, customQuestionField, displayMode, canEditWorkflow, onSuccess, onError, onSelectionChange } =
    props;

  const [questionBuilderDialogOpen, setQuestionBuilderDialogOpen] = useState(false);

  const customQuestionFieldUpdate = useCustomQuestionFieldUpdate({
    onSuccess: handleUpdateSuccess,
    onError: handleUpdateError,
  });
  const customQuestionFieldDelete = useCustomQuestionFieldDelete({
    onSuccess: handleDeleteSuccess,
    onError: handleDeleteError,
  });

  function handleUpdateSuccess() {
    onSuccess?.(
      t("customQuestionLibrary.libraryItemUpdated", {
        val: t("customQuestions.customQuestion"),
      }),
    );
  }

  function handleDeleteSuccess() {
    onSuccess?.(
      t("customQuestionLibrary.libraryItemDeleted", {
        val: t("customQuestions.customQuestion"),
      }),
    );
  }

  function handleUpdateError() {
    onError?.(t("common.dataUpdateError", { val: t("customQuestions.customQuestion") }));
  }

  function handleDeleteError() {
    onError?.(t("common.dataDeleteError", { val: t("customQuestions.customQuestion") }));
  }

  function handleEditButtonClick() {
    setQuestionBuilderDialogOpen(true);
  }

  function handleSaveEditButtonClick(customQuestionField: CustomQuestionField) {
    setQuestionBuilderDialogOpen(false);
    const updatedReusableQuestionField = convertWorkflowQuestionFieldToReusableQuestion(
      customQuestionField,
      customerId,
    );
    customQuestionFieldUpdate.mutate(updatedReusableQuestionField);
  }

  function handleCancelEditClick() {
    setQuestionBuilderDialogOpen(false);
  }

  function handleConfirmDeleteCustomQuestionClick(questionFieldId: number) {
    customQuestionFieldDelete.mutate(questionFieldId);
  }

  return (
    <Box width={"100%"}>
      <Collapse in={!customQuestionFieldDelete.isSuccess}>
        <Card>
          <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={2} margin={1}>
            {canEditWorkflow && (
              <Stack direction="row" alignItems="center">
                {displayMode === "edit" && (
                  <>
                    <CardActions>
                      <DeleteConfirmationButton
                        confirmationDialogHeader={t("common.deleteConfirmationShort", {
                          val: t("customQuestions.question"),
                          transform: "lowercase",
                        })}
                        confirmationDialogMessage={t("customQuestionLibrary.confirmDeleteQuestion")}
                        onConfirmDeleteButtonClick={() =>
                          handleConfirmDeleteCustomQuestionClick(customQuestionField.id)
                        }
                        variant="icon"
                      />
                      <EditButton onClick={handleEditButtonClick} />
                    </CardActions>
                  </>
                )}
                {displayMode === "select" && (
                  <CardActions>
                    <IconButton
                      onClick={() => {
                        onSelectionChange?.(customQuestionField);
                      }}
                    >
                      {props.isSelected ? <CheckCircleIcon color={"success"} /> : <CheckCircleOutline />}
                    </IconButton>
                  </CardActions>
                )}
              </Stack>
            )}
            <Typography>{customQuestionField.questionText}</Typography>
            {canEditWorkflow && (
              <Stack direction="row" alignItems="center" justifyContent="flex-end" flexGrow={1}>
                {customQuestionField.type === UTP2CustomQuestionTypes.Dropdown && (
                  <Dropdown
                    options={[...(customQuestionField.options || []), t("customQuestions.other")]}
                    placeholder={t("customQuestions.dropdown")}
                  />
                )}
                {customQuestionField.type === UTP2CustomQuestionTypes.ExclusiveDropdown && (
                  <Dropdown
                    options={[...(customQuestionField.options || [])]}
                    placeholder={t("customQuestions.dropdown")}
                  />
                )}
                {customQuestionField.type === UTP2CustomQuestionTypes.Date && (
                  <InputText disabled placeholder={t("customQuestions.dateInput")} />
                )}
                {customQuestionField.type === UTP2CustomQuestionTypes.TextInput && (
                  <InputText disabled placeholder={t("customQuestions.textInput")} />
                )}
              </Stack>
            )}
          </Stack>
        </Card>
        {questionBuilderDialogOpen && (
          <CustomQuestionBuilderDialog
            customQuestionField={convertReusableQuestionToWorkflowQuestionField(customQuestionField)}
            open={questionBuilderDialogOpen}
            onConfirm={handleSaveEditButtonClick}
            onCancel={handleCancelEditClick}
          />
        )}
      </Collapse>
    </Box>
  );
}
